import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { confirmar, toast } from '../../lib/toast';

/**
 * Menús de la barra de un listado (rediseño UX fase 4): «Columnas» (qué
 * columnas se ven), «Agrupar» (por qué columna) y «Vista» (vistas guardadas
 * por usuario y pantalla, GET/POST/PUT/DELETE /api/vistas). Los usa DataTable
 * cuando tiene `storageKey`; el estado vive en DataTable, aquí solo la UI.
 */

export type EstadoVista = {
  q: string;
  sk: string | null;
  sd: boolean;
  filtros: Record<string, string>;
  ocultas: string[];
  grupo: string | null;
};
export type Vista = { id: string; pantalla: string; nombre: string; estado: EstadoVista; por_defecto: boolean; updated_at: string };

function Popover({ boton, abierto, onToggle, children, ancho = 'w-64' }: { boton: ReactNode; abierto: boolean; onToggle: (v: boolean) => void; children: ReactNode; ancho?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!abierto) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onToggle(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onToggle(false); };
    document.addEventListener('mousedown', onDoc); document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [abierto, onToggle]);
  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => onToggle(!abierto)} aria-expanded={abierto} className="cursor-pointer whitespace-nowrap rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-50">{boton}</button>
      {abierto && <div className={`absolute left-0 z-30 mt-1 ${ancho} rounded-md border border-slate-200 bg-white p-1.5 text-xs shadow-lg`}>{children}</div>}
    </div>
  );
}

export function ColumnasMenu({ columnas, ocultas, onChange }: { columnas: { key: string; label: string }[]; ocultas: Set<string>; onChange: (next: Set<string>) => void }) {
  const [abierto, setAbierto] = useState(false);
  const visibles = columnas.length - [...ocultas].filter((k) => columnas.some((c) => c.key === k)).length;
  return (
    <Popover abierto={abierto} onToggle={setAbierto} boton={<>Columnas <span className="text-slate-400">{visibles}/{columnas.length}</span> ▾</>}>
      <div className="max-h-72 overflow-y-auto">
        {columnas.map((c) => (
          <label key={c.key} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-slate-50">
            <input type="checkbox" checked={!ocultas.has(c.key)} onChange={(e) => { const n = new Set(ocultas); if (e.target.checked) n.delete(c.key); else n.add(c.key); onChange(n); }} />
            <span className="truncate">{c.label || c.key}</span>
          </label>
        ))}
      </div>
      <div className="mt-1 flex justify-between border-t border-slate-100 px-2 pt-1.5">
        <button type="button" className="cursor-pointer text-accent-700 hover:underline" onClick={() => onChange(new Set())}>Mostrar todas</button>
      </div>
    </Popover>
  );
}

export function AgruparMenu({ opciones, grupo, onChange }: { opciones: { key: string; label: string }[]; grupo: string | null; onChange: (k: string | null) => void }) {
  const [abierto, setAbierto] = useState(false);
  const actual = opciones.find((o) => o.key === grupo);
  return (
    <Popover abierto={abierto} onToggle={setAbierto} ancho="w-56" boton={actual ? <>Agrupar: <b>{actual.label}</b> ▾</> : <>Agrupar ▾</>}>
      <button type="button" onClick={() => { onChange(null); setAbierto(false); }} className={`block w-full cursor-pointer rounded px-2 py-1 text-left hover:bg-slate-50 ${!grupo ? 'font-semibold' : ''}`}>Sin agrupar</button>
      {opciones.map((o) => (
        <button key={o.key} type="button" onClick={() => { onChange(o.key); setAbierto(false); }} className={`block w-full cursor-pointer rounded px-2 py-1 text-left hover:bg-slate-50 ${grupo === o.key ? 'font-semibold' : ''}`}>{o.label}</button>
      ))}
    </Popover>
  );
}

export function useVistas(pantalla: string | undefined) {
  const qc = useQueryClient();
  const lista = useQuery({
    queryKey: ['vistas', pantalla],
    queryFn: () => api.get<{ data: Vista[] }>(`/vistas?pantalla=${encodeURIComponent(pantalla!)}`),
    enabled: !!pantalla,
    staleTime: 60_000,
  });
  const inval = () => qc.invalidateQueries({ queryKey: ['vistas', pantalla] });
  const crear = useMutation({ mutationFn: (b: { nombre: string; estado: EstadoVista; por_defecto: boolean }) => api.post<Vista>('/vistas', { pantalla, ...b }), onSuccess: inval, onError: (e) => toast.error('No se pudo guardar la vista', (e as Error).message) });
  const actualizar = useMutation({ mutationFn: ({ id, ...b }: { id: string; nombre?: string; estado?: EstadoVista; por_defecto?: boolean }) => api.put<Vista>(`/vistas/${id}`, b), onSuccess: inval, onError: (e) => toast.error('No se pudo actualizar la vista', (e as Error).message) });
  const borrar = useMutation({ mutationFn: (id: string) => api.delete<{ ok: true }>(`/vistas/${id}`), onSuccess: inval, onError: (e) => toast.error('No se pudo eliminar la vista', (e as Error).message) });
  return { vistas: lista.data?.data ?? [], cargando: lista.isLoading, crear, actualizar, borrar };
}

export function VistasMenu({ pantalla, vistas, activa, modificada, estadoActual, onAplicar, onReset, crear, actualizar, borrar }: {
  pantalla: string;
  vistas: Vista[];
  activa: Vista | null;
  modificada: boolean;
  estadoActual: EstadoVista;
  onAplicar: (v: Vista) => void;
  onReset: () => void;
  crear: ReturnType<typeof useVistas>['crear'];
  actualizar: ReturnType<typeof useVistas>['actualizar'];
  borrar: ReturnType<typeof useVistas>['borrar'];
}) {
  const [abierto, setAbierto] = useState(false);
  const [nuevo, setNuevo] = useState('');
  const [guardando, setGuardando] = useState(false);
  const etiqueta = activa ? <>{activa.por_defecto ? '★ ' : ''}{activa.nombre}{modificada ? <b title="Vista modificada: no coincide con la guardada">*</b> : ''}</> : <>Vista estándar{modificada ? <b title="Filtros u orden cambiados">*</b> : ''}</>;
  const guardarNueva = async () => {
    const nombre = nuevo.trim();
    if (!nombre) return;
    setGuardando(true);
    try { const v = await crear.mutateAsync({ nombre, estado: estadoActual, por_defecto: vistas.length === 0 }); onAplicar(v); setNuevo(''); setAbierto(false); toast.success(`Vista «${nombre}» guardada`); }
    finally { setGuardando(false); }
  };
  return (
    <Popover abierto={abierto} onToggle={setAbierto} ancho="w-72" boton={<span className={activa ? 'text-accent-700' : ''}>{etiqueta} ▾</span>}>
      <div className="px-2 pb-1 pt-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">Vistas de {pantalla.replace(/-/g, ' ')}</div>
      <button type="button" onClick={() => { onReset(); setAbierto(false); }} className={`block w-full cursor-pointer rounded px-2 py-1 text-left hover:bg-slate-50 ${!activa ? 'font-semibold' : ''}`}>Vista estándar</button>
      {vistas.map((v) => (
        <div key={v.id} className={`group flex items-center gap-1 rounded px-2 py-1 hover:bg-slate-50 ${activa?.id === v.id ? 'font-semibold' : ''}`}>
          <button type="button" onClick={() => { onAplicar(v); setAbierto(false); }} className="min-w-0 flex-1 cursor-pointer truncate text-left">{v.por_defecto ? '★ ' : ''}{v.nombre}</button>
          {!v.por_defecto && <button type="button" title="Hacer predeterminada" onClick={() => actualizar.mutate({ id: v.id, por_defecto: true })} className="cursor-pointer text-slate-400 hover:text-amber-600">☆</button>}
          <button type="button" title="Eliminar vista" onClick={async () => { if (await confirmar({ title: `¿Eliminar la vista «${v.nombre}»?`, confirmLabel: 'Eliminar', danger: true })) { borrar.mutate(v.id); if (activa?.id === v.id) onReset(); } }} className="cursor-pointer text-slate-400 hover:text-bad-600">✕</button>
        </div>
      ))}
      {activa && modificada && (
        <button type="button" onClick={() => { actualizar.mutate({ id: activa.id, estado: estadoActual }, { onSuccess: () => { toast.success(`Vista «${activa.nombre}» actualizada`); setAbierto(false); } }); }} className="mt-1 block w-full cursor-pointer rounded border border-accent-300 bg-accent-50 px-2 py-1 text-left text-accent-700 hover:bg-accent-100">Guardar cambios en «{activa.nombre}»</button>
      )}
      <div className="mt-1.5 border-t border-slate-100 pt-1.5">
        <div className="flex gap-1">
          <input value={nuevo} onChange={(e) => setNuevo(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') guardarNueva(); }} placeholder="Guardar como nueva vista…" className="min-w-0 flex-1 rounded border border-slate-300 px-2 py-1 text-xs" aria-label="Nombre de la vista nueva" />
          <button type="button" disabled={!nuevo.trim() || guardando} onClick={guardarNueva} className="cursor-pointer rounded bg-slate-900 px-2 py-1 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40">Guardar</button>
        </div>
        <p className="mt-1 px-0.5 text-[10px] text-slate-400">Guarda búsqueda, filtros, orden, columnas y agrupación. ★ = se aplica al abrir.</p>
      </div>
    </Popover>
  );
}
